import { SIGN_IN_ERRORS, SIGN_IN_LIMITS, type SignInStep, signInStepSchema } from '@sivan/shared';
import { type ReactNode, type SyntheticEvent, useEffect, useId, useRef, useState } from 'react';
import { ApiError, NetworkError, apiPost } from '../api/client';
import logo from '../assets/sivan-logo.png';
import { Icon } from '../ui/Icon';

// Sign-in in the hosted deployment (AUTH_MODE=supabase): the application's own form. The API
// relays the password and the authenticator code to Supabase and answers with the next step; no
// token ever reaches this page, and nothing here is stored in the browser. Links from invitation
// and recovery emails (`?invite=` / `?reset=`) lead to choosing a password; the token is removed
// from the address bar at once.

type Phase =
  | { readonly kind: 'password' }
  | { readonly kind: 'mfa' }
  | {
      readonly kind: 'enroll';
      readonly qrCode: string;
      readonly secret: string;
      readonly uri: string | null;
    }
  | { readonly kind: 'recover' }
  | { readonly kind: 'recover-sent' }
  | { readonly kind: 'set-password'; readonly mode: 'invite' | 'recovery'; readonly token: string };

const TOKEN = /^[A-Za-z0-9_-]{20,200}$/;

/** An invitation or recovery token from the address, removed from it (and from history) at once. */
function takeLinkToken(): Phase | null {
  const params = new URLSearchParams(window.location.search);
  const invite = params.get('invite');
  const reset = params.get('reset');
  if (invite === null && reset === null) return null;
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash}`);
  const token = invite ?? reset ?? '';
  if (!TOKEN.test(token)) return { kind: 'password' };
  return { kind: 'set-password', mode: invite !== null ? 'invite' : 'recovery', token };
}

class Mismatch extends Error {}

function explain(error: unknown): string {
  if (error instanceof Mismatch) return 'שתי ההקלדות של הסיסמה אינן זהות.';
  if (error instanceof ApiError) {
    const code = error.body['code'];
    if (typeof code === 'string' && Object.hasOwn(SIGN_IN_ERRORS, code))
      return SIGN_IN_ERRORS[code as keyof typeof SIGN_IN_ERRORS];
    if (error.status === 429) return SIGN_IN_ERRORS.too_many;
    if (error.status >= 500) return SIGN_IN_ERRORS.unavailable;
  }
  if (error instanceof NetworkError) return 'אין חיבור לשרת. בדקו את החיבור ונסו שוב.';
  return 'הכניסה לא הושלמה. נסו שוב.';
}

function Card({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <main className="sign-in" id="main-content">
      <div className="sign-in__card">
        <img className="sign-in__logo" src={logo} alt="סיון ביצוע" width={670} height={160} />
        <h1>{title}</h1>
        {children}
        <p className="sign-in__foot">
          <Icon name="lock" />
          הסיסמה והקוד נבדקים בשירות הזהות של הארגון. ההרשאות נקבעות במערכת.
        </p>
      </div>
    </main>
  );
}

function CodeField({ label }: { readonly label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <label className="field">
      <span>{label}</span>
      <input
        ref={ref}
        name="code"
        dir="ltr"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={SIGN_IN_LIMITS.code}
        required
      />
    </label>
  );
}

export function PasswordSignIn({
  reason,
  keptDrafts,
  onSignedIn,
}: {
  readonly reason: 'none' | 'expired';
  /** Unsaved changes are kept in this browser (shown after an expired session). */
  readonly keptDrafts: boolean;
  readonly onSignedIn: () => void;
}) {
  const [phase, setPhase] = useState<Phase>(() => takeLinkToken() ?? { kind: 'password' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const hintId = useId();

  const go = (step: SignInStep) => {
    if (step.step === 'signed-in') onSignedIn();
    else if (step.step === 'mfa') setPhase({ kind: 'mfa' });
    else
      setPhase({
        kind: 'enroll',
        qrCode: step.qrCode,
        secret: step.secret,
        uri: step.uri ?? null,
      });
  };
  const submit =
    (send: (form: FormData) => Promise<void>) =>
    (event: SyntheticEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (busy) return;
      const form = new FormData(event.currentTarget);
      setBusy(true);
      setError(null);
      send(form)
        .catch((e: unknown) => {
          setError(explain(e));
          const code = e instanceof ApiError ? e.body['code'] : null;
          // A pending sign-in that expired starts over from the password.
          if (code === 'expired' || code === 'too_many')
            setPhase((p) => (p.kind === 'mfa' || p.kind === 'enroll' ? { kind: 'password' } : p));
        })
        .finally(() => {
          setBusy(false);
        });
    };
  const text = (form: FormData, name: string) => {
    const value = form.get(name);
    return typeof value === 'string' ? value : '';
  };
  const problem = error !== null && (
    <p className="notice notice--error" role="alert">
      {error}
    </p>
  );

  if (phase.kind === 'mfa' || phase.kind === 'enroll')
    return (
      <Card title={phase.kind === 'enroll' ? 'הגדרת אימות דו־שלבי' : 'אימות דו־שלבי'}>
        {phase.kind === 'enroll' ? (
          <Enrollment qrCode={phase.qrCode} secret={phase.secret} uri={phase.uri} />
        ) : (
          <p className="sign-in__lead">הקלידו את הקוד בן 6 הספרות שמוצג באפליקציית האימות.</p>
        )}
        {problem}
        <form
          className="sign-in__form"
          noValidate={false}
          onSubmit={submit(async (form) => {
            go(await apiPost('/auth/mfa', { code: text(form, 'code').trim() }, signInStepSchema));
          })}
        >
          <CodeField label="קוד אימות" />
          <button className="button button--primary sign-in__button" type="submit" disabled={busy}>
            {busy ? 'בודק…' : 'אישור'}
          </button>
        </form>
      </Card>
    );

  if (phase.kind === 'set-password')
    return (
      <Card title={phase.mode === 'invite' ? 'ברוכים הבאים' : 'בחירת סיסמה חדשה'}>
        <p className="sign-in__lead">
          {phase.mode === 'invite'
            ? 'בחרו סיסמה לחשבון שלכם במערכת. תתבקשו להשתמש בה בכל כניסה.'
            : 'בחרו סיסמה חדשה לחשבון.'}
        </p>
        {problem}
        <form
          className="sign-in__form"
          onSubmit={submit(async (form) => {
            const password = text(form, 'password');
            if (password !== text(form, 'confirm')) throw new Mismatch();
            go(
              await apiPost(
                '/auth/password/set',
                phase.mode === 'invite'
                  ? {
                      kind: phase.mode,
                      token: phase.token,
                      password,
                      email: text(form, 'email').trim(),
                    }
                  : { kind: phase.mode, token: phase.token, password },
                signInStepSchema,
              ),
            );
          })}
        >
          {phase.mode === 'invite' && (
            <label className="field">
              <span>דוא״ל עבודה (הכתובת שההזמנה נוצרה עבורה)</span>
              <input name="email" type="email" dir="ltr" autoComplete="username" required />
            </label>
          )}
          <label className="field">
            <span>סיסמה חדשה</span>
            <input
              name="password"
              type="password"
              dir="ltr"
              autoComplete="new-password"
              minLength={SIGN_IN_LIMITS.password.min}
              maxLength={SIGN_IN_LIMITS.password.max}
              aria-describedby={hintId}
              required
            />
          </label>
          <p className="hint" id={hintId}>
            לפחות {SIGN_IN_LIMITS.password.min} תווים. מומלץ משפט קצר שקל לזכור.
          </p>
          <label className="field">
            <span>הקלדה חוזרת של הסיסמה</span>
            <input
              name="confirm"
              type="password"
              dir="ltr"
              autoComplete="new-password"
              maxLength={SIGN_IN_LIMITS.password.max}
              required
            />
          </label>
          <button className="button button--primary sign-in__button" type="submit" disabled={busy}>
            {busy ? 'שומר…' : 'שמירה וכניסה'}
          </button>
        </form>
      </Card>
    );

  if (phase.kind === 'recover' || phase.kind === 'recover-sent')
    return (
      <Card title="שכחתי סיסמה">
        {phase.kind === 'recover-sent' ? (
          <p className="notice notice--success" role="status">
            אם הכתובת שייכת לחשבון במערכת, נשלח אליה קישור לבחירת סיסמה חדשה. הקישור תקף לזמן מוגבל.
          </p>
        ) : (
          <>
            <p className="sign-in__lead">הקלידו את כתובת הדוא״ל של העבודה, ונשלח אליה קישור.</p>
            {problem}
            <form
              className="sign-in__form"
              onSubmit={submit(async (form) => {
                await apiPost(
                  '/auth/recover',
                  { email: text(form, 'email').trim() },
                  {
                    parse: () => undefined,
                  },
                );
                setPhase({ kind: 'recover-sent' });
              })}
            >
              <label className="field">
                <span>דוא״ל עבודה</span>
                <input name="email" type="email" dir="ltr" autoComplete="username" required />
              </label>
              <button
                className="button button--primary sign-in__button"
                type="submit"
                disabled={busy}
              >
                {busy ? 'שולח…' : 'שליחת קישור'}
              </button>
            </form>
          </>
        )}
        <button
          type="button"
          className="button button--ghost"
          onClick={() => {
            setError(null);
            setPhase({ kind: 'password' });
          }}
        >
          חזרה לכניסה
        </button>
      </Card>
    );

  const expired = reason === 'expired';
  return (
    <Card title={expired ? 'פג תוקף ההתחברות' : 'כניסה למערכת'}>
      {expired && keptDrafts && (
        <p className="notice notice--warning" role="status">
          השינויים שלא נשמרו שמורים בדפדפן זה. התחברו מחדש כדי להמשיך מאותה נקודה.
        </p>
      )}
      {problem}
      <form
        className="sign-in__form"
        onSubmit={submit(async (form) => {
          go(
            await apiPost(
              '/auth/password',
              { email: text(form, 'email').trim(), password: text(form, 'password') },
              signInStepSchema,
            ),
          );
        })}
      >
        <label className="field">
          <span>דוא״ל עבודה</span>
          <input name="email" type="email" dir="ltr" autoComplete="username" required />
        </label>
        <label className="field">
          <span>סיסמה</span>
          <input
            name="password"
            type="password"
            dir="ltr"
            autoComplete="current-password"
            maxLength={SIGN_IN_LIMITS.password.max}
            required
          />
        </label>
        <button className="button button--primary sign-in__button" type="submit" disabled={busy}>
          {busy ? 'נכנס…' : 'כניסה'}
        </button>
      </form>
      <button
        type="button"
        className="button button--ghost"
        onClick={() => {
          setError(null);
          setPhase({ kind: 'recover' });
        }}
      >
        שכחתי סיסמה
      </button>
    </Card>
  );
}

/**
 * A phone or a tablet: the QR code is on the same screen as the authenticator, so it cannot be
 * scanned. A coarse pointer, or a touch screen no wider than a tablet.
 */
function onTouchDevice(): boolean {
  const coarse =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const smallTouch =
    navigator.maxTouchPoints > 0 && Math.min(window.screen.width, window.screen.height) <= 1024;
  return coarse || smallTouch;
}

/**
 * Setting up the authenticator app. On a computer the QR code comes first (scanned with the phone);
 * on a phone, where the code cannot be scanned, opening the authenticator app directly and the
 * key with a Copy button come first. The key is only shown here: never logged, never stored.
 */
function Enrollment({
  qrCode,
  secret,
  uri,
}: {
  readonly qrCode: string;
  readonly secret: string;
  readonly uri: string | null;
}) {
  const [touch] = useState(onTouchDevice);
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
  const copy = () => {
    void navigator.clipboard
      .writeText(secret)
      .then(() => {
        setCopied('yes');
      })
      .catch(() => {
        setCopied('failed');
      });
  };
  const key = (
    <div className="sign-in__key">
      <code className="sign-in__secret" dir="ltr" aria-label="מפתח ההגדרה">
        {secret.match(/.{1,4}/g)?.join(' ') ?? secret}
      </code>
      <button className="button" type="button" onClick={copy}>
        <Icon name="clipboard" /> העתקת המפתח
      </button>
      <span className="hint" role="status">
        {copied === 'yes'
          ? 'המפתח הועתק. הדביקו אותו באפליקציית האימות.'
          : copied === 'failed'
            ? 'ההעתקה לא הצליחה. סמנו את המפתח והעתיקו אותו ידנית.'
            : ''}
      </span>
    </div>
  );
  const qr = <img className="sign-in__qr" src={qrCode} alt="קוד QR להגדרת אפליקציית האימות" />;
  return (
    <>
      <p className="sign-in__lead">
        לחשבון זה נדרשת אפליקציית אימות (למשל Google Authenticator או Microsoft Authenticator). היא
        מציגה קוד בן 6 ספרות שמתחלף כל חצי דקה, ותתבקשו להקליד אותו בכל כניסה.
      </p>
      {touch ? (
        <>
          <ol className="sign-in__steps">
            {uri !== null && (
              <li>
                <a className="button button--primary sign-in__button" href={uri}>
                  פתיחה באפליקציית האימות
                </a>
                <span className="hint">אם האפליקציה לא נפתחה, השתמשו במפתח שבהמשך.</span>
              </li>
            )}
            <li>
              {uri !== null ? 'או: ' : ''}באפליקציית האימות בחרו &quot;הוספת חשבון&quot; ←
              &quot;הזנת מפתח&quot;, הדביקו את המפתח, ובחרו &quot;מבוסס זמן&quot;.
              {key}
            </li>
            <li>חזרו לכאן והקלידו את הקוד בן 6 הספרות שהאפליקציה מציגה.</li>
          </ol>
          <details className="sign-in__more">
            <summary>האפליקציה במכשיר אחר? סריקת קוד QR</summary>
            {qr}
          </details>
        </>
      ) : (
        <>
          <p className="hint">סרקו את הקוד באפליקציה בטלפון, ואז הקלידו כאן את הקוד שהיא מציגה.</p>
          {qr}
          <details className="sign-in__more">
            <summary>אין אפשרות לסרוק? הזנת מפתח ידנית</summary>
            {key}
          </details>
        </>
      )}
    </>
  );
}
